///
/// EarthBrowser
///
/// Created by Matt Giger on September 13, 2026
/// Copyright (c) 2026 Geodata Labs
///

/// Factory reset: completely wipe all browser-side EarthBrowser state so a
/// customer can rebuild from scratch (last-resort customer support tool).
///
/// The wipe must happen BEFORE the app renders, because the SQLite/GeoPackage
/// workers hold FileSystemSyncAccessHandle locks on OPFS files once they start,
/// which makes OPFS deletion fail. The flow is:
///
///   1. requestFactoryReset() sets a sessionStorage marker and reloads
///   2. main.tsx calls runPendingFactoryReset() before rendering anything
///   3. With no workers running, everything is deletable: OPFS, IndexedDB,
///      localStorage, sessionStorage, and client-visible cookies
///   4. The page reloads once more into a completely fresh state
///
/// A hidden URL trigger (?factory-reset) covers the case where the app is too
/// broken to boot far enough to reach the Storage Manager UI. Support can tell
/// a customer to visit the app with that query parameter; a native confirm()
/// gate prevents drive-by resets from malicious links.
///
/// NOTE: this module runs before React and must not import app modules
/// (store, logger, etc.) to avoid side effects, so it logs via console.

const FACTORY_RESET_MARKER = 'eb-factory-reset-pending'
const FACTORY_RESET_URL_PARAM = 'factory-reset'

/// IndexedDB databases known to be created by the app, used as a fallback when
/// indexedDB.databases() is unavailable (older Firefox/Safari)
const KNOWN_INDEXED_DBS = [
	'EarthBrowserHandles',
	'EarthBrowserCache',
	'EarthBrowserFileSystem',
	'EarthBrowserViewThumbnails',
	'earthbrowser-git-fs',
	'earthbrowser-git-fs_lock',
]

export interface FactoryResetReport {
	opfsDeleted: string[]
	opfsFailed: string[]
	indexedDBsDeleted: string[]
	indexedDBsFailed: string[]
	localStorageCleared: boolean
	sessionStorageCleared: boolean
	cookiesCleared: boolean
}

/// Request a factory reset from application UI. Sets the pending marker and
/// reloads; the actual wipe happens on the next boot before anything renders.
export function requestFactoryReset(): void {
	sessionStorage.setItem(FACTORY_RESET_MARKER, '1')
	window.location.reload()
}

function hasUrlTrigger(): boolean {
	return new URLSearchParams(window.location.search).has(FACTORY_RESET_URL_PARAM)
}

/// Lightweight check for whether a reset might run on this boot
export function isFactoryResetPending(): boolean {
	return sessionStorage.getItem(FACTORY_RESET_MARKER) === '1' || hasUrlTrigger()
}

function stripUrlTrigger(): string {
	const url = new URL(window.location.href)
	url.searchParams.delete(FACTORY_RESET_URL_PARAM)
	return url.toString()
}

type DirectoryHandleEntries = AsyncIterable<[string, FileSystemFileHandle | FileSystemDirectoryHandle]>

async function wipeOPFS(report: FactoryResetReport): Promise<void> {
	if (!navigator.storage?.getDirectory) return

	try {
		const root = await navigator.storage.getDirectory()
		const names: string[] = []
		for await (const [name] of root as unknown as DirectoryHandleEntries) {
			names.push(name)
		}
		for (const name of names) {
			try {
				await root.removeEntry(name, { recursive: true })
				report.opfsDeleted.push(name)
			} catch (error) {
				report.opfsFailed.push(name)
				console.warn(`[factory-reset] Failed to delete OPFS entry ${name}:`, error)
			}
		}
	} catch (error) {
		console.warn('[factory-reset] OPFS wipe failed:', error)
	}
}

function deleteIndexedDB(name: string): Promise<void> {
	return new Promise((resolve, reject) => {
		// Guard against a deletion hanging forever on a blocked connection
		const timeout = setTimeout(() => reject(new Error('Deletion timed out')), 5000)
		const request = indexedDB.deleteDatabase(name)
		request.onsuccess = () => {
			clearTimeout(timeout)
			resolve()
		}
		request.onerror = () => {
			clearTimeout(timeout)
			reject(request.error ?? new Error('Failed to delete database'))
		}
		request.onblocked = () => {
			clearTimeout(timeout)
			reject(new Error('Deletion blocked by an open connection'))
		}
	})
}

async function wipeIndexedDBs(report: FactoryResetReport): Promise<void> {
	if (!('indexedDB' in window)) return

	let names = KNOWN_INDEXED_DBS
	try {
		if (typeof indexedDB.databases === 'function') {
			const databases = await indexedDB.databases()
			const enumerated = databases.map(db => db.name).filter((n): n is string => !!n)
			// Union with the known list in case enumeration is incomplete
			names = Array.from(new Set([...enumerated, ...KNOWN_INDEXED_DBS]))
		}
	} catch (error) {
		console.warn('[factory-reset] Failed to enumerate IndexedDB databases:', error)
	}

	for (const name of names) {
		try {
			await deleteIndexedDB(name)
			report.indexedDBsDeleted.push(name)
		} catch (error) {
			report.indexedDBsFailed.push(name)
			console.warn(`[factory-reset] Failed to delete IndexedDB ${name}:`, error)
		}
	}
}

function wipeCookies(report: FactoryResetReport): void {
	try {
		const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT'
		for (const cookie of document.cookie.split(';')) {
			const name = cookie.split('=')[0]?.trim()
			if (!name) continue
			// Expire for both the current path and root path
			document.cookie = `${name}=; ${expiry}; path=/`
			document.cookie = `${name}=; ${expiry}`
		}
		report.cookiesCleared = true
	} catch (error) {
		console.warn('[factory-reset] Failed to clear cookies:', error)
	}
}

/// Perform the complete wipe. Callers must ensure no workers have been started
/// yet (i.e. call before rendering the app).
export async function performFactoryReset(): Promise<FactoryResetReport> {
	const report: FactoryResetReport = {
		opfsDeleted: [],
		opfsFailed: [],
		indexedDBsDeleted: [],
		indexedDBsFailed: [],
		localStorageCleared: false,
		sessionStorageCleared: false,
		cookiesCleared: false,
	}

	// Clear the marker first so a failure part-way through can't reset-loop
	sessionStorage.removeItem(FACTORY_RESET_MARKER)

	await wipeOPFS(report)
	await wipeIndexedDBs(report)

	try {
		localStorage.clear()
		report.localStorageCleared = true
	} catch (error) {
		console.warn('[factory-reset] Failed to clear localStorage:', error)
	}

	try {
		sessionStorage.clear()
		report.sessionStorageCleared = true
	} catch (error) {
		console.warn('[factory-reset] Failed to clear sessionStorage:', error)
	}

	wipeCookies(report)

	console.info('[factory-reset] Complete:', report)
	return report
}

/// Check for and execute a pending factory reset. Called from the boot path in
/// main.tsx before anything renders. Returns true if a reset ran and the page
/// is about to reload (caller should not render).
export async function runPendingFactoryReset(): Promise<boolean> {
	const markerSet = sessionStorage.getItem(FACTORY_RESET_MARKER) === '1'
	const urlTriggered = hasUrlTrigger()

	if (!markerSet && !urlTriggered) {
		return false
	}

	// The URL trigger is for customer support ("visit this link to reset").
	// Gate it behind a native confirm so a malicious link can't silently wipe
	// a customer's data. The in-app path already confirmed, so skip it there.
	if (!markerSet && urlTriggered) {
		const proceed = window.confirm(
			'EarthBrowser Factory Reset\n\n' +
			'This will permanently erase ALL EarthBrowser data stored in this browser: ' +
			'databases, imported files, settings, and caches. ' +
			'Your account data on the server is not affected.\n\n' +
			'Continue?'
		)
		if (!proceed) {
			// Strip the trigger and boot normally
			window.history.replaceState(null, '', stripUrlTrigger())
			return false
		}
	}

	await performFactoryReset()

	// Reload into a clean URL (strips ?factory-reset when present)
	window.location.replace(stripUrlTrigger())
	return true
}
